import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { api, errorDetails, errorMessage } from '../lib/api';
import { Rubric } from '../exam/Rubric';
import { PlacedLabels } from './PlacedLabels';
import {
  formatDuration,
  type ReviewGroup,
  type ReviewPart,
  type ReviewQuestion,
  type TestDetail,
  type TestSummary,
  withReadiness,
} from '../lib/tests';
import { PageHeader } from '../components/PageHeader';
import { Banner } from '../components/Banner';
import { DiagnosticList } from '../components/DiagnosticList';
import { AssignTest } from '../components/AssignTest';
import { SetNameField } from '../components/SetNameField';
import { PassageEditor } from '../components/PassageEditor';
import { writingPromptHtml } from '../exam/writing';

export function TestReviewPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<TestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** What the import did with any pictures chosen beside the paste. */
  const attached = (useLocation().state as { attached?: string[] } | null)?.attached ?? [];
  const [notice, setNotice] = useState<string | null>(null);
  /** Where an uploaded recording actually went, when it was not obvious. */
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showFindings, setShowFindings] = useState(false);
  /** Every paper in the library, so this one knows the test it belongs to. */
  const [library, setLibrary] = useState<TestSummary[]>([]);

  const load = useCallback(async () => {
    try {
      const [one, all] = await Promise.all([
        api.get<TestDetail>(`/tests/${id}`),
        api.get<{ tests: TestSummary[] }>('/tests'),
      ]);
      setDetail(one.data);
      setLibrary(all.data.tests);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load this paper'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  /**
   * Ask again whether the paper is ready, and nothing else.
   *
   * Correcting an answer key is one small save, and reloading the whole paper
   * after every one of them would rebuild the table a teacher is typing down.
   * This asks the one question the screen goes stale on — is it publishable
   * yet — and leaves the rows where they are.
   */
  const refreshReadiness = useCallback(async () => {
    try {
      const { data } = await api.get<TestDetail>(`/tests/${id}`);
      setDetail((prev) => (prev ? withReadiness(prev, data.test.readiness) : prev));
    } catch {
      // The save itself already said whether it worked. A panel that cannot be
      // re-read is not worth a second error on top of that.
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <p className="muted">Loading…</p>;
  if (!detail) return <Banner tone="error">{error ?? 'Paper not found.'}</Banner>;

  const { test, parts } = detail;
  const { readiness, importReport } = test;
  /**
   * Whether this centre may change the paper or only teach from it. A test
   * lent to the platform by another centre is read-only here — the server
   * refuses every edit, so the screen should not offer one.
   */
  const owned = test.owned !== false;

  /**
   * The papers of the test this one belongs to, in the order they are sat.
   *
   * A test is set as a whole, so the panel below needs all three rather than
   * the one on screen — including this one, which the library also holds.
   */
  const ORDER = ['LISTENING', 'READING', 'WRITING'];
  /** The tests this centre already has, so the box completes rather than typos. */
  const knownTests = [
    ...new Set(library.map((paper) => paper.setName).filter((name): name is string => Boolean(name))),
  ].sort();
  /**
   * The papers of this test, one per module, in the order they are sat.
   *
   * Same name is not enough on its own any more: a centre can hold its own
   * "Test 1" and a "Test 1" lent by another centre, and it can hold an older
   * spare of a module beside the one it uses. Setting a test with either of
   * those in it gives a candidate two Listening papers to sit.
   */
  const siblings = test.setName
    ? ORDER.flatMap((module) => {
        const found = library
          .filter(
            (paper) =>
              paper.setName === test.setName &&
              paper.module === module &&
              (paper.owned !== false) === (test.owned !== false),
          )
          // Newest first, which is the one this centre is teaching from.
          .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
        const chosen = found.find((paper) => paper.id === id) ?? found[0];
        return chosen ? [{ id: chosen.id, module: chosen.module, status: chosen.status }] : [];
      })
    : [];

  async function publish() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/tests/${id}/publish`);
      setNotice('Published. You can now assign this paper to your students.');
      await load();
    } catch (err) {
      const problems = errorDetails<{ problems?: string[] }>(err)?.problems;
      setError(problems?.length ? problems.join(' ') : errorMessage(err, 'Could not publish'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Attaching a file, optionally to a named part.
   *
   * Naming the part is what makes four recordings four recordings: without it
   * every upload went to the paper as a whole and the second silently replaced
   * the first.
   */
  async function uploadAsset(file: File, partIndex?: number) {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const form = new FormData();
      form.append('file', file);
      if (partIndex !== undefined) form.append('partIndex', String(partIndex));
      const { data } = await api.post<{ note: string | null }>(`/tests/${id}/assets`, form);
      // Where the file went, when that is not where the teacher might assume.
      if (data.note) setNote(data.note);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'That file could not be attached'));
    } finally {
      setBusy(false);
    }
  }

  const errorCount = importReport?.diagnostics.filter((d) => d.severity === 'error').length ?? 0;

  return (
    <>
      <PageHeader
        eyebrow={<Link to="/tests">← Test library</Link>}
        title={test.title}
        meta={
          <>
            <p className="muted">
              {test.module === 'READING'
                ? `Reading · ${test.variant === 'GENERAL' ? 'General Training' : 'Academic'}`
                : 'Listening'}{' '}
              · <span className="mono">{readiness.questionCount}/40</span> questions ·{' '}
              <span className="mono">{formatDuration(test.durationMs)}</span>
            </p>
            <span className={`tag ${test.status === 'PUBLISHED' ? 'tag--on' : ''}`}>
              {test.status}
            </span>
          </>
        }
        actions={
          // Publishing is the owner's, like everything else that changes the
          // paper. A borrowed one is already published or it would not be in
          // anybody else's library.
          !owned || test.status === 'PUBLISHED' ? undefined : (
            /*
             * A disabled button that does not say why is a dead end. The
             * reasons were already on the page, in a panel below this — far
             * enough down that the question people actually asked was "why
             * can't I publish?" rather than "how do I fix these two things?".
             */
            <div className="publish">
              <button
                className="btn btn--primary"
                disabled={busy || !readiness.ready}
                onClick={() => void publish()}
              >
                Publish to students
              </button>
              {!readiness.ready && (
                <a className="publish__why" href="#before-publishing">
                  {readiness.problems.length}{' '}
                  {readiness.problems.length === 1 ? 'thing' : 'things'} to fix first
                </a>
              )}
            </div>
          )
        }
      />

      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}
        {notice && <Banner tone="success">{notice}</Banner>}
        {note && <Banner tone="warning">{note}</Banner>}

        {attached.length > 0 && (
          <Banner tone="success">
            <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
              {attached.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </Banner>
        )}

        {owned && !readiness.ready && (
          <div className="panel" id="before-publishing">
            <h2 className="panel__title">Before this paper can be published</h2>
            {readiness.questionCount === 0 && (
              <p className="muted" style={{ maxWidth: '64ch' }}>
                Nothing in what was imported was read as a question. That is usually the part
                headings: check that lines like “PART 1” and “Questions 1–10” came through the
                paste, and that the question numbers are at the start of their lines.
              </p>
            )}
            <ul className="checklist">
              {readiness.problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Not a fault, but the sort of thing that is only noticed mid-sitting
            if nobody says it here. */}
        {owned && readiness.warnings?.length ? (
          <div className="panel">
            <h2 className="panel__title">Worth knowing</h2>
            <ul className="checklist">
              {readiness.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {/*
          * What a centre is looking at when the paper is not theirs.
          *
          * Said once, at the top, because everything below reads the same
          * either way and the difference decides whether half of it will do
          * anything at all.
          */}
        {!owned && (
          <Banner tone="info">
            {test.platform
              ? 'This is one of the platform’s own papers. Every centre teaches from it, and it is changed by the platform rather than by any one centre — including this one.'
              : 'This test is shared with every centre on the platform. You can set it on your classes and read it here; the centre that made it is the one that can change it.'}
          </Banner>
        )}

        {owned && <AssetPanel test={test} parts={parts} busy={busy} onUpload={uploadAsset} />}

        {owned && (
          <SetNameField
            testId={id}
            setName={test.setName ?? null}
            known={knownTests}
            onSaved={() => void load()}
          />
        )}

        {/*
          * A test is set, not a paper. A paper belonging to no test cannot be
          * given to anybody until it is part of one — which is what the box
          * above is for.
          */}
        {test.setName ? (
          <AssignTest
            setName={test.setName}
            papers={siblings}
            onDone={() => void load()}
          />
        ) : (
          <section className="panel">
            <h2 className="panel__title">Assigning</h2>
            <p className="muted" style={{ maxWidth: '62ch' }}>
              A test is set on a class as a whole — its Listening, its Reading and its Writing
              together — so this paper needs to be part of one before anybody can sit it. Name the
              test above.
            </p>
          </section>
        )}

        {/* What the importer made of the paste — a note to whoever did the
            importing, and nothing to a centre that borrowed the result. */}
        {owned && importReport && (
          <section className="panel">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h2 className="panel__title" style={{ marginBottom: 0 }}>
                Import findings
              </h2>
              <button className="btn btn--ghost" onClick={() => setShowFindings((v) => !v)}>
                {showFindings ? 'Hide' : 'Show'} ({importReport.diagnostics.length})
              </button>
            </div>
            <p className="muted" style={{ marginTop: 'var(--s-2)' }}>
              Read from <span className="mono">{importReport.sourceFilename}</span>
              {errorCount > 0 && ' — the line numbers refer to that file.'}
            </p>
            {showFindings && (
              <div style={{ marginTop: 'var(--s-4)' }}>
                {importReport.notes.map((note) => (
                  <p key={note} className="muted">
                    {note}
                  </p>
                ))}
                <DiagnosticList diagnostics={importReport.diagnostics} />
              </div>
            )}
          </section>
        )}

        <section className="stack">
          <div>
            <p className="eyebrow">The paper</p>
            <p className="muted" style={{ maxWidth: '62ch' }}>
              This is what your students will see, with the answer key shown alongside.
              {owned
                ? ' Correct any answer here — changes save as you leave the box.'
                : ' The centre that made this test is the one that can correct it.'}
            </p>
          </div>

          {parts.map((part) => (
            <article key={part.id} className="paper rise">
              <header className="paper__head">
                <p className="eyebrow">
                  {test.module === 'LISTENING' ? `Part ${part.index}` : `Passage ${part.index}`}
                </p>
                {part.passageTitle && <h3 className="paper__title">{part.passageTitle}</h3>}
                {part.instructions && <p className="muted">{part.instructions}</p>}
              </header>

              {part.passageHtml && (
                <>
                  <div
                    className="passage"
                    // Server-generated from recognised text — no markup from the
                    // uploaded document survives the importer.
                    dangerouslySetInnerHTML={{ __html: part.passageHtml }}
                  />
                  {/* Where the paragraphs are is the one thing about a passage
                      the importer has to guess, so it is also the one thing
                      this screen can put right. */}
                  {owned && (
                    <PassageEditor testId={id} part={part} onSaved={() => void load()} />
                  )}
                </>
              )}

              {part.groups.map((group) => (
                <GroupBlock
                  key={group.id}
                  testId={id}
                  group={group}
                  variant={test.variant === 'GENERAL' ? 'GENERAL' : 'ACADEMIC'}
                  editable={owned}
                  onChanged={() => void load()}
                  onQuestionSaved={() => void refreshReadiness()}
                />
              ))}
            </article>
          ))}
        </section>
      </div>
    </>
  );
}

/**
 * The files a paper refers to.
 *
 * A Listening paper gets a slot per part, always — not only when the imported
 * document happened to name a file for each. Centres hold their audio both
 * ways, and the one button that used to be here sent every upload to the paper
 * as a whole, so the second recording replaced the first and the third
 * replaced that. Four files went in and one came out.
 */
function AssetPanel({
  test,
  parts,
  busy,
  onUpload,
}: {
  test: TestDetail['test'];
  parts: ReviewPart[];
  busy: boolean;
  onUpload: (file: File, partIndex?: number) => Promise<void>;
}) {
  const input = useRef<HTMLInputElement | null>(null);
  const missing = test.readiness.missingAssets;
  /** Which part the next file picked is for; undefined means the whole paper. */
  /**
   * Which part the next file picked is for; undefined means the whole paper.
   *
   * A ref rather than state, and that is the whole point of it. Setting state
   * and opening the picker in the same breath leaves the change handler
   * closing over the previous value — the file dialog usually takes long
   * enough for React to have re-rendered, so it works by luck, and the once it
   * does not the recording lands on the wrong part with nothing to say so.
   */
  const target = useRef<number | undefined>(undefined);

  const perPart = parts.some((part) => part.audio) || parts.some((part) => part.audioFilename);
  const whole = !perPart && test.audio.length > 0 ? test.audio[0] : null;

  function pick(partIndex?: number) {
    target.current = partIndex;
    input.current?.click();
  }

  return (
    <section className="panel stack">
      <div>
        <h2 className="panel__title">Recording and artwork</h2>
        <p className="muted" style={{ maxWidth: '62ch' }}>
          {test.module === 'LISTENING'
            ? 'A paper holds either one recording of the whole test or one for each part. Upload against a part and it goes there; images are matched to their questions by filename.'
            : 'Upload the files your paper refers to. Images are matched to the right questions by their filename.'}
        </p>
      </div>

      {test.module === 'LISTENING' && (
        <div className="stack" style={{ gap: 'var(--s-3)' }}>
          {/* The whole-paper recording, while that is the shape of the paper.
              Uploading against a part turns it into a per-part paper, and this
              row is replaced by the four below. */}
          {!perPart && (
            <div className="slotrow">
              <div>
                <span className="field__label">The whole test</span>
                {whole ? (
                  <p className="mono">
                    {whole.filename} · {formatDuration(whole.durationMs)}
                  </p>
                ) : (
                  <p className="muted">
                    Not uploaded
                    {test.importReport?.expectedAudioFilename
                      ? ` — the paper asks for ${test.importReport.expectedAudioFilename}`
                      : ''}
                    .
                  </p>
                )}
                {whole && <audio controls preload="none" src={whole.url} style={{ width: '100%' }} />}
              </div>
              {/* Part 0 is the whole test: said explicitly so that replacing a
                  recording is a replacement rather than "and here is Part 2". */}
              <button className="btn" disabled={busy} onClick={() => pick(0)}>
                {whole ? 'Replace' : 'Upload'}
              </button>
            </div>
          )}

          {parts.map((part) => (
            <div key={part.id} className="slotrow">
              <div>
                <span className="field__label">Part {part.index}</span>
                {part.audio ? (
                  <>
                    <p className="mono">
                      {part.audio.filename} · {formatDuration(part.audio.durationMs)}
                    </p>
                    <audio
                      controls
                      preload="none"
                      src={part.audio.url}
                      style={{ width: '100%' }}
                    />
                  </>
                ) : (
                  <p className="muted">
                    No recording
                    {part.audioFilename ? ` — the paper asks for ${part.audioFilename}` : ''}.
                  </p>
                )}
              </div>
              <button className="btn" disabled={busy} onClick={() => pick(part.index)}>
                {part.audio ? 'Replace' : 'Upload'}
              </button>
            </div>
          ))}

          {test.audio.length > 1 && (
            <p className="field__hint">
              These play one after another, as a single continuous recording would. The exam lasts
              their combined length plus two minutes of checking time, as in the real test.
            </p>
          )}
        </div>
      )}

      {(test.importReport?.expectedImageFilenames.length ?? 0) > 0 && (
        <div className="assetrow">
          <span className="field__label">Images</span>
          <ul className="checklist">
            {test.importReport!.expectedImageFilenames.map((filename) => {
              const outstanding = missing.some((m) => m.filename === filename);
              return (
                <li key={filename} className={outstanding ? '' : 'checklist__done'}>
                  <span className="mono">{filename}</span> {outstanding ? '— not uploaded' : '— attached'}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <input
        ref={input}
        type="file"
        /* Types and endings both. A picker that maps .m4a or .opus to nothing
           greys the file out, and the teacher is left unable to choose a
           recording that is sitting right in front of them. */
        accept="audio/*,image/*,.mp3,.m4a,.m4b,.aac,.wav,.ogg,.oga,.opus,.flac,.wma,.aiff,.mp4,.mov"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          const partIndex = target.current;
          target.current = undefined;
          e.target.value = '';
          if (file) void onUpload(file, partIndex);
        }}
      />
      <div className="row">
        <button className="btn" disabled={busy} onClick={() => pick(undefined)}>
          Upload a file
        </button>
        {test.module === 'LISTENING' && (
          <p className="field__hint" style={{ margin: 0 }}>
            Or use the button beside a part, for a paper with a recording each.
          </p>
        )}
      </div>
    </section>
  );
}

function GroupBlock({
  testId,
  group,
  variant,
  editable,
  onChanged,
  onQuestionSaved,
}: {
  testId: string;
  group: ReviewGroup;
  variant: 'ACADEMIC' | 'GENERAL';
  /** Reload the paper: a change of type redraws the whole group. */
  onChanged: () => void;
  /** Re-read whether the paper is publishable: one question just changed. */
  onQuestionSaved: () => void;
  /**
   * False for a test lent by another centre: the paper is theirs, the server
   * refuses every change to it, and a box that silently fails to save an
   * answer key is worse than no box.
   */
  editable: boolean;
}) {
  const [maxWords, setMaxWords] = useState<number | null>(group.maxWords);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Free-text answers only: a letter has no word count, and the server ignores
  // the field for those types anyway.
  const wordLimitApplies = group.inputMode === 'GAP' || group.inputMode === 'TEXT';

  /**
   * The stored body back as the text it was written from.
   *
   * The gaps go back to {24}, which is how the template writes them and how
   * the box below expects them — a teacher edits what they typed, not markup.
   */
  const bodyAsText = (group.bodyHtml ?? '')
    .replace(/<span class="gap" data-gap="(\d+)"><\/span>/g, '{$1}')
    // Headings and list points back to how they are typed, so rewriting a
    // text does not run its points together into one line.
    .replace(/<h4 class="q-heading">/g, '# ')
    .replace(/<li class="q-sub">/g, '    - ')
    .replace(/<li>/g, '• ')
    .replace(/<\/(p|tr|h[1-6]|li)>/g, '\n')
    .replace(/<\/t[dh]>/g, ' | ')
    .replace(/<[^>]*>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  /**
   * A group whose gaps have no text to sit in. A typed gap always needs one;
   * a word-bank or drag task needs one when its questions carry no wording of
   * their own — a summary completed from a box, imported without the summary.
   */
  const textIsMissing =
    !group.bodyHtml &&
    (group.inputMode === 'GAP' ||
      ((group.inputMode === 'DRAG' || group.inputMode === 'BANK') &&
        group.questions.every((q) => !q.promptHtml)));
  const [editingBody, setEditingBody] = useState(false);
  const [bodyText, setBodyText] = useState(bodyAsText);

  async function saveBody() {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/tests/${testId}/groups/${group.id}`, { bodyText });
      setEditingBody(false);
      onChanged();
    } catch (err) {
      setError(errorMessage(err, 'Could not save that text'));
    } finally {
      setSaving(false);
    }
  }

  const bank = group.optionsBank ?? [];
  /** The kinds of task a candidate answers from a box of lettered options. */
  const takesABox = group.inputMode === 'DRAG' || group.inputMode === 'GRID' || group.inputMode === 'BANK';
  const [editingOptions, setEditingOptions] = useState(false);
  const [optionsText, setOptionsText] = useState(() =>
    bank.map((option) => `${option.letter}  ${option.text}`).join('\n'),
  );

  /**
   * Relabelling the task, when the importer read it as the wrong kind.
   *
   * The paper is reloaded rather than patched in place: the type decides how
   * the whole group is drawn — a grid, a bank, gaps in a body — so the screen
   * has to be rebuilt from what the server now holds rather than from a
   * guess about what changed.
   */
  async function saveType(next: string) {
    if (next === group.type) return;
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/tests/${testId}/groups/${group.id}`, { type: next });
      onChanged();
    } catch (err) {
      setError(errorMessage(err, 'Could not change the kind of task'));
    } finally {
      setSaving(false);
    }
  }

  /**
   * The box of options, pasted again.
   *
   * A paper whose box was printed in columns imports as two options carrying
   * the other four inside their text, and until now the only cure was
   * importing the whole paper a second time — losing the marking and the
   * sittings already against it. This is one part of one group.
   *
   * The paper is reloaded afterwards, like a relabelling: the bank decides how
   * the group is drawn.
   */
  async function saveOptions() {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/tests/${testId}/groups/${group.id}`, { optionsText });
      setEditingOptions(false);
      onChanged();
    } catch (err) {
      setError(errorMessage(err, 'Could not read that box of options'));
    } finally {
      setSaving(false);
    }
  }

  async function saveLimit(next: number | null) {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/tests/${testId}/groups/${group.id}`, { maxWords: next });
      setMaxWords(next);
      group.maxWords = next;
    } catch (err) {
      setError(errorMessage(err, 'Could not change the word limit'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="qgroup">
      <header className="qgroup__head">
        <p className="eyebrow">
          Questions {group.fromQ}–{group.toQ} · {group.typeLabel}
        </p>

        {/*
          * What kind of task this is.
          *
          * The importer works it out from the rubric and is usually right, but
          * a paper that words a task unusually comes in labelled as something
          * else — and the label decides how a candidate answers it: a grid of
          * ticks or a bank of letters to drag. Until now the only way to
          * correct one was to import the whole paper again.
          *
          * The list is the server's, not this screen's: only tasks answered
          * the same way are offered, because the answers are already stored
          * and a relabelling has to leave them meaning what they meant.
          */}
        {editable && (group.alternatives?.length ?? 0) > 0 && (
          <div className="row" style={{ marginTop: 'var(--s-3)' }}>
            <span className="field__label">Kind of task</span>
            <select
              className="field__input"
              style={{ width: 'auto' }}
              value={group.type}
              disabled={saving}
              onChange={(e) => void saveType(e.target.value)}
              aria-label={`Kind of task for questions ${group.fromQ} to ${group.toQ}`}
            >
              <option value={group.type}>{group.typeLabel}</option>
              {group.alternatives!.map((option) => (
                <option key={option.type} value={option.type}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className="field__hint">
              Changing this changes how candidates answer it. The answers already saved are kept.
            </span>
          </div>
        )}
        {group.instructions && <Rubric text={group.instructions} />}

        {/*
          * The box, as the paper printed it.
          *
          * Shown only where the group has one. A teacher who can see that the
          * box came in wrong can put it right here rather than importing the
          * paper again — which is the difference between a two-minute fix and
          * losing every sitting already recorded against the paper.
          */}
        {/*
          * The summary, the notes, the table — the text the gaps sit in.
          *
          * The last part of a group that could not be corrected, which meant a
          * section whose body came in wrongly took the whole paper with it.
          */}
        {/*
          * Or the text that never arrived: notes completion imported as bare
          * numbered boxes, each one a dash and a gap, with the notes they
          * belong in nowhere on the screen.
          */}
        {editable && (group.bodyHtml || textIsMissing) && (
          <div className="stack" style={{ marginTop: 'var(--s-3)' }}>
            {!editingBody ? (
              <div className="row" style={{ gap: 'var(--s-3)', alignItems: 'baseline' }}>
                <span className="field__label">
                  {group.bodyHtml ? 'The text' : 'No notes, summary or table came in with these questions'}
                </span>
                <button className="btn btn--ghost" onClick={() => {
                  setBodyText(group.bodyHtml ? bodyAsText : startingText(group));
                  setEditingBody(true);
                }}>
                  {group.bodyHtml ? 'Rewrite the text' : 'Add the text'}
                </button>
              </div>
            ) : (
              <div className="stack">
                <span className="field__label">
                  {group.inputMode === 'ESSAY'
                    ? 'The task, as the candidate reads it — a blank line starts a new paragraph'
                    : <>
                        Each gap written as {'{'}24{'}'}, where the answer goes — or paste it from
                        the paper, with its gaps printed as “24 ..........”
                      </>}
                </span>
                <textarea
                  className="writer__editor"
                  spellCheck={false}
                  rows={8}
                  value={bodyText}
                  disabled={saving}
                  onChange={(e) => setBodyText(e.target.value)}
                  aria-label={`The text for questions ${group.fromQ} to ${group.toQ}`}
                />
                <div className="row" style={{ gap: 'var(--s-3)' }}>
                  <button className="btn" disabled={saving} onClick={() => void saveBody()}>
                    Save the text
                  </button>
                  <button
                    className="btn btn--ghost"
                    disabled={saving}
                    onClick={() => setEditingBody(false)}
                  >
                    Cancel
                  </button>
                </div>
                {group.inputMode !== 'ESSAY' && (
                <span className="field__hint">
                  A line starting <span className="mono"># </span> is a heading, one starting{' '}
                  <span className="mono">• </span> is a point, and one pushed in with spaces and a{' '}
                  <span className="mono">- </span> is a point under it. A row of a table is written with pipes:
                  <span className="mono"> | Location | Job title |</span>. Every question in this
                  group needs a gap, or the change is refused.
                </span>
                )}
              </div>
            )}
          </div>
        )}

        {editable &&
          ['GAP', 'TEXT', 'CHOICE_ONE', 'CHOICE_MANY'].includes(group.inputMode) && (
            <ChooseLetters testId={testId} group={group} onDone={onChanged} />
          )}

        {/* A matching task answered from a box has one even when the import
            lost it — so the box can be added, not only pasted again. */}
        {editable && (bank.length > 0 || takesABox) && (
          <div className="stack" style={{ marginTop: 'var(--s-3)' }}>
            {!editingOptions ? (
              <div className="row" style={{ gap: 'var(--s-3)', alignItems: 'baseline' }}>
                <span className="field__label">Options</span>
                <span className="mono">
                  {bank.length > 0 ? `${bank.length} in the box` : 'none — candidates have nothing to choose from'}
                </span>
                <button className="btn btn--ghost" onClick={() => setEditingOptions(true)}>
                  {bank.length > 0 ? 'Paste the box again' : 'Add the box of options'}
                </button>
              </div>
            ) : (
              <div className="stack">
                <span className="field__label">One option per line, as “A  some text”</span>
                <textarea
                  className="field__input"
                  rows={Math.min(12, Math.max(4, bank.length + 1))}
                  value={optionsText}
                  disabled={saving}
                  onChange={(e) => setOptionsText(e.target.value)}
                />
                <div className="row" style={{ gap: 'var(--s-3)' }}>
                  <button className="btn" disabled={saving} onClick={() => void saveOptions()}>
                    Save the box
                  </button>
                  <button
                    className="btn btn--ghost"
                    disabled={saving}
                    onClick={() => {
                      setOptionsText(bank.map((o) => `${o.letter}  ${o.text}`).join('\n'));
                      setEditingOptions(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
                <span className="field__hint">
                  A row printed across the page — “A one B two C three” — is read as three options.
                  Answers already given are kept, and a letter one of them needs cannot be removed.
                </span>
              </div>
            )}
          </div>
        )}
        {wordLimitApplies && editable && (
          <div className="row" style={{ marginTop: 'var(--s-3)' }}>
            <span className="field__label">Word limit</span>
            <select
              className="field__input"
              style={{ width: 'auto' }}
              value={maxWords ?? ''}
              disabled={saving}
              onChange={(e) => void saveLimit(e.target.value ? Number(e.target.value) : null)}
              aria-label={`Word limit for questions ${group.fromQ} to ${group.toQ}`}
            >
              <option value="">No limit</option>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  No more than {n} word{n === 1 ? '' : 's'}
                </option>
              ))}
            </select>
            <span className="field__hint">
              An answer over the limit is marked wrong, as in the real test.
            </span>
          </div>
        )}
        {error && <p className="field__error">{error}</p>}
      </header>

      {/* A paper that places its own gaps is edited on the picture; one that
          prints its numbers into the artwork is just shown. */}
      {group.imageUrl && group.questions.some((q) => q.at) && editable ? (
        <PlacedLabels testId={testId} group={group} />
      ) : (
        group.imageUrl && (
          <figure className="qgroup__figure">
            <img src={group.imageUrl} alt={group.imageFilename ?? 'Diagram'} />
          </figure>
        )
      )}

      {group.optionsBank && (
        <ul className="bank">
          {group.optionsBank.map((option) => (
            <li key={option.letter}>
              <span className="bank__letter mono">{option.letter}</span>
              {option.text}
            </li>
          ))}
        </ul>
      )}

      {group.bodyHtml && (
        <div
          className="qbody"
          /*
           * Through the same function the exam uses, so a teacher checking a
           * Writing paper reads what a candidate will read — including the
           * fixed sentences the screen supplies when the paper was typed
           * without them. Showing the raw text here meant the review screen
           * and the exam disagreed about what the paper said.
           */
          dangerouslySetInnerHTML={{
            __html: group.type.startsWith('WRITING_TASK')
              ? writingPromptHtml(
                  group.bodyHtml,
                  group.type === 'WRITING_TASK_2' ? 2 : 1,
                  variant,
                )
              : group.bodyHtml,
          }}
        />
      )}

      <div className="ledger__wrap">
        <table className="ledger">
          <thead>
            <tr>
              <th className="num">No.</th>
              <th>Question</th>
              <th>Accepted answer</th>
            </tr>
          </thead>
          <tbody>
            {group.questions
              // A question worth several marks is one row, as the candidate
              // sees it — its numbers share one prompt, one list and one key.
              .filter((question) => question.spanFrom == null || question.spanFrom === question.number)
              .map((question) => (
              <QuestionRow
                // Keyed by what it shows as well as by id, so a change made to
                // the whole group — rebuilt, relabelled — redraws the rows from
                // what the server now holds rather than what they started with.
                key={`${question.id}:${group.type}:${question.answer.raw}:${question.promptHtml ?? ''}:${question.options?.length ?? 0}`}
                testId={testId}
                group={group}
                question={question}
                editable={editable}
                onSaved={onQuestionSaved}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Where a teacher starts when the text is missing: one line per question,
 * holding whatever wording it has and its gap, ready to be written around.
 */
function startingText(group: ReviewGroup): string {
  return group.questions
    .map((q) => {
      const words = (q.promptHtml ?? '').replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').trim();
      return words ? `${words} {${q.number}}` : `{${q.number}}`;
    })
    .join('\n');
}

function QuestionRow({
  testId,
  group,
  question,
  editable,
  onSaved,
}: {
  testId: string;
  group: ReviewGroup;
  question: ReviewQuestion;
  /** False for a borrowed paper: the key is shown, not offered for editing. */
  editable: boolean;
  /**
   * Called once a change to this question has been stored, so the list of
   * things still to fix — and the Publish button it governs — can be asked
   * again. Without it a teacher fixes the last problem on the list and the
   * list carries on naming it.
   */
  onSaved: () => void;
}) {
  const [raw, setRaw] = useState(question.answer.raw);
  const [accepted, setAccepted] = useState(question.answer.accepted);
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  async function save() {
    if (raw.trim() === question.answer.raw.trim() && state !== 'error') return;
    setState('saving');
    try {
      const { data } = await api.patch<{ question: { answer: { raw: string; accepted: string[] } } }>(
        `/tests/${testId}/questions/${question.number}`,
        { answerRaw: raw },
      );
      setAccepted(data.question.answer.accepted);
      question.answer.raw = data.question.answer.raw;
      setState('saved');
      setMessage(null);
      onSaved();
    } catch (err) {
      setState('error');
      setMessage(errorMessage(err, 'Could not save that answer'));
    }
  }

  /** The stored wording as text, which is what the box is filled with. */
  const plainPrompt = (question.promptHtml ?? '')
    .replace(/<[^>]*>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .trim();
  const [promptText, setPromptText] = useState(plainPrompt);
  const [savingPrompt, setSavingPrompt] = useState(false);

  async function savePrompt() {
    if (promptText.trim() === plainPrompt.trim()) return;
    setSavingPrompt(true);
    try {
      const { data } = await api.patch<{ question: { promptHtml: string | null } }>(
        `/tests/${testId}/questions/${question.number}`,
        { promptText },
      );
      question.promptHtml = data.question.promptHtml;
      setMessage(null);
      onSaved();
    } catch (err) {
      setState('error');
      setMessage(errorMessage(err, 'Could not save that wording'));
    } finally {
      setSavingPrompt(false);
    }
  }

  /**
   * A multiple-choice question's own options, editable — and addable, where
   * the import lost them and candidates were shown a question with nothing to
   * choose from. One per line, as they are printed: "A  some text".
   */
  const isChoice = group.inputMode === 'CHOICE_ONE' || group.inputMode === 'CHOICE_MANY';
  const [options, setOptions] = useState(question.options ?? []);
  const asText = (list: { letter: string; text: string }[]) =>
    list.map((option) => `${option.letter}  ${option.text}`).join('\n');
  const [editingOptions, setEditingOptions] = useState(false);
  const [optionsText, setOptionsText] = useState(() => asText(question.options ?? []));
  const [savingOptions, setSavingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  async function saveOptions() {
    setSavingOptions(true);
    setOptionsError(null);
    try {
      const { data } = await api.patch<{ question: { options: { letter: string; text: string }[] | null } }>(
        `/tests/${testId}/questions/${question.number}`,
        { optionsText },
      );
      const saved = data.question.options ?? [];
      setOptions(saved);
      question.options = saved;
      setOptionsText(asText(saved));
      setEditingOptions(false);
      onSaved();
    } catch (err) {
      setOptionsError(errorMessage(err, 'Could not save those options'));
    } finally {
      setSavingOptions(false);
    }
  }

  /**
   * Whether the wording lives on the question itself. In a gap task it is
   * written in the group's text above, which has its own editor; everywhere
   * else a question the import left without words can be given them here.
   */
  const ownsWording =
    Boolean(question.promptHtml) || (!group.bodyHtml && group.inputMode !== 'DRAG');

  return (
    <tr>
      <td className="num">
        {question.spanTo != null && question.spanTo !== question.number
          ? `${question.number}–${question.spanTo}`
          : question.number}
      </td>
      <td>
        {/*
          * The wording, correctable in place.
          *
          * A paste brings a printed answer line with it — "School | ........"
          * — and taking it out used to mean importing the paper again. Plain
          * text goes up; the server escapes it and builds the markup, so what
          * a teacher can change is the words and never the tags.
          */}
        {/*
          * The wording, in a box you can type in.
          *
          * This was a double-click on the text, with a dotted underline on
          * hover to hint at it — which nobody finds, least of all somebody
          * working through a list of problems the paper has. It is a field
          * now, beside the answer field, and behaves the same way: type, and
          * it saves when you leave it.
          *
          * A question whose wording lives in the group's text above has no
          * field here, because that is not where it is written.
          */}
        {editable && ownsWording ? (
          <input
            className="field__input"
            value={promptText}
            disabled={savingPrompt}
            onChange={(e) => setPromptText(e.target.value)}
            onBlur={() => void savePrompt()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') {
                setPromptText(plainPrompt);
                e.currentTarget.blur();
              }
            }}
            placeholder="The question's wording"
            aria-label={`Wording of question ${question.number}`}
          />
        ) : question.promptHtml ? (
          <span dangerouslySetInnerHTML={{ __html: question.promptHtml }} />
        ) : (
          <span className="muted">
            {group.inputMode === 'GAP' || group.inputMode === 'DRAG'
              ? 'gap in the text above'
              : '—'}
          </span>
        )}
        {options.length > 0 && !editingOptions && (
          <ul className="choices">
            {options.map((option) => (
              <li key={option.letter}>
                <span className="mono">{option.letter}</span> {option.text}
              </li>
            ))}
          </ul>
        )}
        {editable && isChoice && !editingOptions && (
          <button className="btn btn--ghost btn--sm" onClick={() => setEditingOptions(true)}>
            {options.length > 0 ? 'Edit the options' : 'Add the options'}
          </button>
        )}
        {editable && isChoice && editingOptions && (
          <div className="stack" style={{ gap: 'var(--s-2)', marginTop: 'var(--s-2)' }}>
            <textarea
              className="field__input"
              rows={Math.max(4, options.length + 1)}
              value={optionsText}
              disabled={savingOptions}
              placeholder={'A  first option\nB  second option\nC  third option\nD  fourth option'}
              onChange={(e) => setOptionsText(e.target.value)}
              aria-label={`Options for question ${question.number}`}
            />
            <span className="field__hint">One option per line, as “A  some text”.</span>
            {optionsError && <p className="field__error">{optionsError}</p>}
            <div className="row" style={{ gap: 'var(--s-2)' }}>
              <button className="btn btn--sm" disabled={savingOptions} onClick={() => void saveOptions()}>
                {savingOptions ? 'Saving…' : 'Save the options'}
              </button>
              <button
                className="btn btn--ghost btn--sm"
                disabled={savingOptions}
                onClick={() => {
                  setOptionsText(asText(options));
                  setOptionsError(null);
                  setEditingOptions(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </td>
      <td>
        {editable ? (
          <input
            className="field__input mono"
            value={raw}
            onChange={(e) => {
              setRaw(e.target.value);
              setState('idle');
            }}
            onBlur={() => void save()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
            aria-label={`Accepted answer for question ${question.number}`}
          />
        ) : (
          <p className="mono" style={{ margin: 0 }}>
            {raw}
          </p>
        )}
        {state === 'error' ? (
          <p className="field__error">{message}</p>
        ) : (
          // Showing the expansion is the point of the review screen: it is how
          // an instructor sees that "(the) Baker | Baker's" really does accept
          // the three forms they had in mind.
          <p className="field__hint">
            {state === 'saving' ? 'Saving…' : `accepts: ${accepted.join(' · ')}`}
          </p>
        )}
      </td>
    </tr>
  );
}

/**
 * Rebuilding a group as one "Choose THREE letters, A-G" question.
 *
 * For the task the importer reads as something else — typing boxes with no
 * prompt and no options, each keyed "B/F/G". The prompt, the list and the
 * letters are given once here, and every number in the group shares them.
 */
function ChooseLetters({
  testId,
  group,
  onDone,
}: {
  testId: string;
  group: ReviewGroup;
  onDone: () => void;
}) {
  const count = group.toQ - group.fromQ + 1;
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [optionsText, setOptionsText] = useState('');
  const [answer, setAnswer] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Already one question across the whole group: nothing to rebuild.
  const alreadyOne = group.questions.every(
    (q) => q.spanFrom === group.fromQ && q.spanTo === group.toQ,
  );
  // The importer's mistake has a signature: every number keyed the same
  // handful of letters, "B/F/G" three times. Anything else is a real task and
  // the offer would only be noise on it.
  const keys = new Set(group.questions.map((q) => q.answer.raw.trim().toUpperCase()));
  const lettersKeyed =
    keys.size === 1 && /^[A-Z](\s*[\/,|&\s]\s*(AND\s+)?[A-Z])+$/.test([...keys][0] ?? '');
  if (count < 2 || alreadyOne || !lettersKeyed) return null;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/tests/${testId}/groups/${group.id}/choose-letters`, {
        prompt,
        optionsText,
        answer,
      });
      setOpen(false);
      onDone();
    } catch (err) {
      setError(errorMessage(err, 'Could not rebuild these questions'));
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <div className="row" style={{ marginTop: 'var(--s-3)', gap: 'var(--s-3)', alignItems: 'baseline' }}>
        <span className="muted">
          Is this one “Choose {count} letters” question on the paper?
        </span>
        <button className="btn btn--ghost" onClick={() => setOpen(true)}>
          Rebuild as a choose-letters question
        </button>
      </div>
    );
  }

  return (
    <div className="panel stack" style={{ marginTop: 'var(--s-3)', gap: 'var(--s-3)' }}>
      <p className="muted" style={{ margin: 0 }}>
        Questions {group.fromQ}–{group.toQ} become one question: candidates choose {count} letters,
        one mark for each right letter. The answers below replace the current ones.
      </p>
      <label className="field">
        <span className="field__label">The question</span>
        <input
          className="field__input"
          value={prompt}
          placeholder="Which THREE of the following are given as advice?"
          onChange={(e) => setPrompt(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field__label">The options, one per line</span>
        <textarea
          className="field__input"
          rows={8}
          value={optionsText}
          placeholder={'A  first option\nB  second option\nC  third option'}
          onChange={(e) => setOptionsText(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field__label">The {count} right letters</span>
        <input
          className="field__input mono"
          value={answer}
          placeholder={count === 3 ? 'B F G' : 'B F'}
          onChange={(e) => setAnswer(e.target.value)}
        />
      </label>
      {error && <p className="field__error">{error}</p>}
      <div className="row" style={{ gap: 'var(--s-2)' }}>
        <button className="btn btn--primary" disabled={saving} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Rebuild the questions'}
        </button>
        <button className="btn btn--ghost" disabled={saving} onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}
